import { Box, Grid, Link, Stack, styled, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { StyledParams } from 'types/common';
import { ButtonCustom } from 'app/components/elements/ButtonCustom';
import { InputText } from 'app/components/elements/InputText';
import { useInjection } from './hook';
import { Icon_Arrow } from 'assets';
import { TextEllipsis } from 'app/components/elements/TextEllipsis';
import { Controller } from 'react-hook-form';
import { DatePicker } from '../../components/elements/DatePicker';
import dayjs from 'dayjs';
import { InputSelect } from '../../components/elements/InputSelect';
import { SelectChangeEvent } from '@mui/material/Select';
import { getNameOption } from 'utils/convertData';
import { lowerCase, startCase } from 'lodash';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import { useFilteredRoomContext } from 'hooks/useFilteredRoomContext';
import { dropdownValueTypes } from 'const/option';
import { entriesInclude } from 'utils/entries';
import { Image } from 'antd';
import { sharedImagePreviewConfig } from 'utils/antImagePreviewEnhancements';

export const RoomReportViewPhotoPage = () => {
  const {
    paths,
    refInputFile,
    register,
    handleSubmit,
    errors,
    control,
    handleSelectReport,
    reportList,
    issuesId,
    setValue,
    watch,
    onDeleteIssue,
    photos,
    onUploadFile,
    newPhotos,
    onDeleteNewPhoto,
    onDeleteActivePhoto
  } = useInjection();
  const { systemDropdownValues, singleEntryField, users } = useFilteredRoomContext();
  const reportIssueDropdownOption = useMemo(() => {
    const options = systemDropdownValues?.filter((item) =>
      item.options.some((option) => option.type === dropdownValueTypes.reportIssue)
    );
    return options;
  }, [systemDropdownValues]);

  const roomLevelIssueOptionSingleField = useMemo(() => {
    return singleEntryField?.filter((item) =>
      entriesInclude(item.entries, dropdownValueTypes.reportIssue)
    );
  }, [singleEntryField]);
  return (
    <WrapperCreatePage>
      <Stack
        width={'100%'}
        flexDirection={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}>
        <TitlePageStyled>Report</TitlePageStyled>
        <ButtonCustom
          sx={{ width: 'max-content', height: '46px', padding: '0 20px' }}
          label={'Back'}
          onClick={() => {
            window.history.go(-1);
          }}
        />
      </Stack>
      <Stack mt={0.5} gap={2.5} direction="row">
        {paths.map((path, index) => (
          <Stack
            key={index}
            flexDirection={'row'}
            alignItems={'center'}
            justifyContent={'center'}
            gap={2}
            mr={0.5}>
            <TextPath fontWeight={paths.length === index + 1 ? '900' : '500'}>{path}</TextPath>
            {index < paths.length - 1 && <Icon_Arrow color={'#226AAA'} />}
          </Stack>
        ))}
      </Stack>

      <Box sx={{ display: 'flex', gap: 3, mt: 1, width: '100%' }}>
        {/* SIDEBAR: REPORT LIST */}
        <Stack
          sx={{
            width: 320,
            flexShrink: 0,
            gap: 2,
            maxHeight: 'calc(100vh - 200px)',
            overflowY: 'auto',
            pr: 1
          }}>
          <Typography variant="h6" fontWeight={700} sx={{ color: '#226AAA', mb: 1 }}>
            Other Reports
          </Typography>
          {reportList?.map((report) => (
            <ReportListItem
              key={report.id}
              active={Number(issuesId) === report.id}
              onClick={() => handleSelectReport(report.id)}>
              <Stack gap={0.5}>
                <Typography variant="body1" fontWeight={700}>
                  {report.name || report.equipmentId}
                </Typography>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {dayjs(report.createdAt).format('MMM DD, YYYY')}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: report.status === 'ACTIVE' ? 'error.main' : 'success.main',
                      fontWeight: 700
                    }}>
                    {report.status}
                  </Typography>
                </Stack>
              </Stack>
            </ReportListItem>
          ))}
        </Stack>

        {/* MAIN CONTENT: FORM AND PHOTOS */}
        <Stack
          sx={{
            flex: 1,
            borderRadius: '10px',
            border: '1px solid rgba(144, 144, 144, 0.20)',
            padding: '20px',
            backgroundColor: '#fff',
            maxHeight: 'calc(100vh - 200px)',
            overflowY: 'auto'
          }}>
          <Stack flexDirection={'row'} gap={3} justifyContent={'space-between'}>
            <Grid container spacing={2.5}>
              <Grid item xs={6} container spacing={4.5}>
                <Image.PreviewGroup>
                  {photos?.map((item, index) => {
                    return (
                      <Grid xs={4} item key={item.id} style={{ position: 'relative' }}>
                        <Stack gap={1.5}>
                          <Image
                            src={item.url}
                            alt={item?.photo?.fileName || 'photo'}
                            style={{
                              width: '100%',
                              objectFit: 'contain',
                              display: 'block',
                              cursor: 'pointer'
                            }}
                            preview={sharedImagePreviewConfig}
                          />
                          <TextEllipsis
                            text={item?.photo?.fileName}
                            width={'95%'}
                            line={12}
                            styles={{ padding: '0 8px' }}
                          />
                        </Stack>
                        <BtnDeletePhoto
                          onClick={() => {
                            onDeleteActivePhoto(index);
                          }}
                        />
                      </Grid>
                    );
                  })}
                  {newPhotos?.map((item, index) => {
                    return (
                      <Grid item xs={4} key={item.name} style={{ position: 'relative' }}>
                        <Stack gap={1.5}>
                          <Image
                            src={URL.createObjectURL(item)}
                            alt={item.name}
                            style={{
                              width: '100%',
                              objectFit: 'contain',
                              display: 'block',
                              cursor: 'pointer'
                            }}
                            preview={sharedImagePreviewConfig}
                          />
                          <TextEllipsis
                            text={item.name}
                            width={'95%'}
                            line={12}
                            styles={{ padding: '0 8px' }}
                          />
                        </Stack>
                        <BtnDeletePhoto
                          onClick={() => {
                            onDeleteNewPhoto(index);
                          }}
                        />
                      </Grid>
                    );
                  })}
                </Image.PreviewGroup>
                <Grid item xs={4}>
                  <Box
                    onClick={() => refInputFile.current?.click()}
                    sx={{
                      width: '100%',
                      aspectRatio: 1,
                      border: '2px dashed #ccc',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      borderRadius: '8px',
                      bgcolor: '#f8f9fa',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: '#e3f2fd',
                        borderColor: '#1976d2'
                      }
                    }}>
                    <AddPhotoAlternateIcon sx={{ color: '#888', fontSize: 32, mb: 1 }} />
                  </Box>
                  <input
                    accept={'image/*'}
                    type="file"
                    style={{ display: 'none' }}
                    ref={refInputFile}
                    onChange={onUploadFile}
                  />
                </Grid>
              </Grid>
              <Grid item xs={6}>
                <Stack
                  gap={2}
                  flex={1}
                  alignItems={'flex-start'}
                  justifyContent={'flex-start'}
                  px={4}>
                  <InputText
                    title={'Equipment Id'}
                    inputError={errors.equipmentId?.message}
                    placeholder={'Enter Equipment Id'}
                    inputProps={{
                      ...register('equipmentId')
                    }}
                  />
                  <InputText
                    title={'Name'}
                    inputError={errors.name?.message}
                    placeholder={'Enter name'}
                    inputProps={{
                      ...register('name')
                    }}
                  />
                  <InputText
                    title={'Description'}
                    inputError={errors.description?.message}
                    placeholder={'Enter Description'}
                    inputProps={{
                      ...register('description')
                    }}
                  />
                  {reportIssueDropdownOption?.map((opt) => (
                    <React.Fragment key={opt.id}>
                      <Controller
                        control={control}
                        name={`option_${opt.id}`}
                        render={({ field: { onChange, value }, fieldState }) => (
                          <InputSelect
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            title={`${startCase(lowerCase(getNameOption(opt as any)))}:`}
                            inputError={fieldState.error?.message}
                            value={value}
                            placeholder="Select Issue Type"
                            onChange={(val) => {
                              onChange(val);
                            }}
                            options={opt?.dropdownValues?.map((item) => ({
                              label: item.value,
                              value: item.value
                            }))}
                          />
                        )}
                      />

                      {watch(`option_${opt.id}`) === 'OTHER' && (
                        <Controller
                          name={`option_${opt.id}_name`}
                          control={control}
                          render={({ field, fieldState }) => (
                            <InputText
                              title="Other Issue"
                              placeholder="Enter Other Issue"
                              inputError={fieldState.error?.message}
                              inputProps={{ ...field }}
                            />
                          )}
                        />
                      )}
                      {watch(`option_${opt.id}`) === 'Action By' && (
                        <>
                          <Controller
                            name={`actionBy`}
                            control={control}
                            render={({ field, fieldState }) => (
                              <InputSelect
                                title="Action By:"
                                placeholder="Select User"
                                value={field.value}
                                inputError={fieldState.error?.message}
                                onChange={field.onChange}
                                options={[
                                  ...(users?.map((item) => ({
                                    label: item.label,
                                    value: item.label
                                  })) ?? []),
                                  { label: 'Other', value: 'OTHER' }
                                ]}
                              />
                            )}
                          />
                          <Stack gap={'10px'} width={'100%'}>
                            <Controller
                              control={control}
                              render={({ field: { value }, fieldState }) => (
                                <DatePicker
                                  inputError={fieldState?.error?.message || ''}
                                  style={{ flexDirection: 'column' }}
                                  title={'Choose Action By Date'}
                                  onChange={(val) =>
                                    setValue('actionByDate', val ? dayjs(val).toISOString() : '')
                                  }
                                  value={value ? dayjs(value) : undefined}
                                />
                              )}
                              name={'actionByDate'}
                            />
                          </Stack>
                        </>
                      )}
                    </React.Fragment>
                  ))}
                  {roomLevelIssueOptionSingleField?.map((field) => (
                    <Stack style={{ width: '100%' }} key={field.id}>
                      {field.type === 'DATE' ? (
                        <Controller
                          name={`single_${field.id}`}
                          control={control}
                          render={({ field: f }) => (
                            <DatePicker
                              title={`${field.name}:`}
                              onChange={(val) =>
                                setValue(
                                  `single_${field.id}`,
                                  val ? dayjs(val).format('YYYY-MM-DD') : ''
                                )
                              }
                              value={f.value ? dayjs(f?.value as string) : undefined}
                            />
                          )}
                        />
                      ) : (
                        <Controller
                          name={`single_${field.id}`}
                          control={control}
                          render={({ field: f, fieldState }) => (
                            <InputText
                              title={`${field.name}:`}
                              placeholder={`Enter ${field.name}`}
                              inputProps={{ ...f }}
                              inputError={fieldState.error?.message}
                            />
                          )}
                        />
                      )}
                    </Stack>
                  ))}
                  {watch('actionBy') === 'OTHER' && (
                    <InputText
                      title={'Action By Name'}
                      inputError={errors.actionByName?.message}
                      placeholder={'Enter Action By Name'}
                      inputProps={{
                        ...register('actionByName')
                      }}
                    />
                  )}

                  <Controller
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <InputSelect
                        title={'Status'}
                        inputError={errors.status?.message || ''}
                        value={value}
                        placeholder={'Status'}
                        onChange={(e: SelectChangeEvent<unknown>) => {
                          onChange(e.target.value);
                          if (e.target.value === 'RESOLVED') {
                            setValue('resolutionDate', dayjs().toISOString());
                          }
                        }}
                        options={[
                          { value: 'ACTIVE', label: 'Open' },
                          { value: 'RESOLVED', label: 'Resolved' }
                        ]}
                      />
                    )}
                    name={`status`}
                  />
                  {watch('status') === 'RESOLVED' && (
                    <Stack style={{ width: '100%' }}>
                      <Controller
                        control={control}
                        render={({ field: { value } }) => (
                          <DatePicker
                            inputError={errors?.resolutionDate?.message}
                            style={{ flexDirection: 'column' }}
                            title={'Choose Resolution Date'}
                            onChange={(val) => {
                              const date = dayjs(val);
                              if (!val || !date.isValid()) {
                                setValue('resolutionDate', '');
                              } else {
                                setValue('resolutionDate', date.toISOString());
                              }
                            }}
                            value={value ? dayjs(value) : undefined}
                          />
                        )}
                        name={'resolutionDate'}
                      />
                    </Stack>
                  )}
                  <Stack style={{ width: '100%' }}>
                    <Controller
                      control={control}
                      render={({ field: { value } }) => (
                        <DatePicker
                          inputError={errors?.actionByDate?.message}
                          style={{ flexDirection: 'column' }}
                          title={'Choose Action By Date'}
                          onChange={(val) => {
                            const date = dayjs(val);
                            if (!val || !date.isValid()) {
                              setValue('actionByDate', '');
                            } else {
                              setValue('actionByDate', date.toISOString());
                            }
                          }}
                          value={value ? dayjs(value) : undefined}
                        />
                      )}
                      name={'actionByDate'}
                    />
                  </Stack>
                  <Controller
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <InputSelect
                        onChange={onChange}
                        inputError={errors.actionBy?.message || ''}
                        title={'Action By'}
                        value={value}
                        placeholder={'Select a user'}
                        options={[
                          ...(users?.map((item) => ({
                            label: item.label,
                            value: item.label
                          })) ?? []),
                          { label: 'Other', value: 'OTHER' }
                        ]}
                      />
                    )}
                    name={`actionBy`}
                  />
                  <InputText
                    title={'Issue Notes'}
                    inputError={errors.note?.message}
                    placeholder={'Enter Issue Notes'}
                    inputProps={{
                      ...register('note')
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: 2, width: '100%', mt: 2 }}>
                    <ButtonCustom style={{ width: 166 }} onClick={handleSubmit} label={'Save'} />
                    <ButtonCustom style={{ width: 166 }} onClick={onDeleteIssue} label={'Delete'} />
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        </Stack>
      </Box>
    </WrapperCreatePage>
  );
};

const WrapperCreatePage = styled(Stack)(() => ({
  padding: '40px 30px 50px 40px',
  width: '100%',
  minHeight: '100vh',
  gap: '20px'
}));
const TitlePageStyled = styled(Typography)(({ theme }: StyledParams) => ({
  margin: '20px 0',
  color: theme.colors.base,
  fontSize: '24px',
  fontWeight: 900,
  fontFamily: `'Avenir',sans-serif`
}));
const TextPath = styled(Link)(() => ({
  fontSize: '16px',
  color: '#226AAA',
  fontFamily: `'Avenir',sans-serif`,
  textDecoration: 'none'
}));
const ReportListItem = styled(Stack)(({ active }: { active: boolean }) => ({
  padding: '16px',
  borderRadius: '12px',
  cursor: 'pointer',
  backgroundColor: active ? 'rgba(34, 106, 170, 0.08)' : '#fff',
  border: active ? `1px solid #226AAA` : '1px solid rgba(144, 144, 144, 0.15)',
  boxShadow: active ? '0px 4px 12px rgba(34, 106, 170, 0.1)' : 'none',
  transition: 'all 0.3s ease',
  position: 'relative',
  // '&:hover': {
  //   backgroundColor: active ? 'rgba(34, 106, 170, 0.12)' : 'rgba(144, 144, 144, 0.05)',
  //   borderColor: active ? '#226AAA' : 'rgba(34, 106, 170, 0.4)',
  //   transform: 'translateY(-2px)'
  // },
  '&::before': active
    ? {
        content: '""',
        position: 'absolute',
        left: 0,
        top: '20%',
        bottom: '20%',
        width: '4px',
        backgroundColor: '#226AAA',
        borderRadius: '0 4px 4px 0'
      }
    : {}
}));
const BtnDeletePhoto = styled(DeleteIcon)({
  position: 'absolute',
  top: 40,
  right: 0,
  color: 'white',
  zIndex: '9999',
  cursor: 'pointer',
  background: 'linear-gradient(rgba(50, 50, 93, 0.25), rgba(0, 0, 0, 0.3))',
  boxShadow: 'rgba(50, 50, 93, 0.25) 0px 13px 27px -5px, rgba(0, 0, 0, 0.3) 0px 8px 16px -8px'
});
